import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomModal from '../custom_modal';
import RequiredReasonTextarea from '../required_reason_textarea';
import { care_api_error, format_ist } from './care_request_rules';
import { resolve_care_request } from './care_request_api';
import {
  answer_label,
  build_resolve_payload,
  new_resolve_draft,
  validate_resolve_draft,
  who_asked_view
} from './who_asked_rules';

const ANSWERS = ['STUDENT', 'TEACHER'];

// PROD-7828 resolve step: Care or the payments team sets the final "who asked" and/or the move a Care note belongs
// to, with a required reason. Teacher pay follows the final "who asked" (the month recalculates).
function ResolveDialog({ record_uuid, who_asked, match, parts, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => new_resolve_draft(who_asked));
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, error: '' });

  useEffect(() => {
    setDraft(new_resolve_draft(who_asked));
    setErrors({});
    setState({ busy: false, error: '' });
  }, [record_uuid, who_asked]);

  const save = async () => {
    const found = validate_resolve_draft(draft, parts);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setState({ busy: true, error: '' });
    try {
      await resolve_care_request(record_uuid, build_resolve_payload(draft, parts));
      setState({ busy: false, error: '' });
      onSaved();
    } catch (e) {
      setState({ busy: false, error: care_api_error(e, 'Could not save. Try again.').message });
    }
  };

  const view = who_asked_view(who_asked);
  return (
    <CustomModal show={!!record_uuid} close={() => (state.busy ? null : onClose())}>
      {record_uuid ? (
        <div role="dialog" aria-label="Resolve change request" className="resolve-dialog">
          <h5>Resolve</h5>
          {parts.answer ? (
            <fieldset className="resolve-dialog-group">
              <legend>Who asked for the change? (final answer)</legend>
              {view.detail ? <p className="resolve-dialog-muted">{view.detail}</p> : null}
              {ANSWERS.map((v) => (
                <label key={v} className="resolve-dialog-option" htmlFor={`resolve-answer-${v}`}>
                  <input
                    id={`resolve-answer-${v}`}
                    type="radio"
                    name="resolved_value"
                    value={v}
                    checked={draft.resolved_value === v}
                    onChange={() => setDraft({ ...draft, resolved_value: v })}
                  />{' '}
                  {answer_label(v)}
                </label>
              ))}
            </fieldset>
          ) : null}
          {parts.match ? (
            <fieldset className="resolve-dialog-group">
              <legend>Which move does this note belong to?</legend>
              {((match && match.candidates) || []).map((c) => (
                <label
                  key={c.audit_id}
                  className="resolve-dialog-option"
                  htmlFor={`resolve-move-${c.audit_id}`}
                >
                  <input
                    id={`resolve-move-${c.audit_id}`}
                    type="radio"
                    name="matched_audit_id"
                    value={c.audit_id}
                    checked={String(draft.matched_audit_id) === String(c.audit_id)}
                    onChange={() => setDraft({ ...draft, matched_audit_id: c.audit_id })}
                  />{' '}
                  {format_ist(c.at)}
                  {(c.changes || []).map(
                    (ch) => ` · ${ch.label}: ${ch.from || '--'} → ${ch.to || '--'}`
                  )}
                </label>
              ))}
            </fieldset>
          ) : null}
          {errors.choice ? <p className="resolve-dialog-error">* {errors.choice}</p> : null}
          <RequiredReasonTextarea
            label="Reason"
            value={draft.reason}
            onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            placeholder="Why is this the right answer? (required)"
            error={!!errors.reason}
            errorText={errors.reason}
            height="60px"
          />
          {state.error ? <p className="resolve-dialog-error">* {state.error}</p> : null}
          <button type="button" disabled={state.busy} onClick={save}>
            {state.busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : null}
    </CustomModal>
  );
}

ResolveDialog.propTypes = {
  record_uuid: PropTypes.string,
  who_asked: PropTypes.shape({ state: PropTypes.string, resolution: PropTypes.shape({}) }),
  match: PropTypes.shape({
    state: PropTypes.string,
    candidates: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  parts: PropTypes.shape({ answer: PropTypes.bool, match: PropTypes.bool }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

ResolveDialog.defaultProps = {
  record_uuid: '',
  who_asked: null,
  match: null
};

export default ResolveDialog;
